import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { GetJobCodesJobCodePayload, GetJobCodesJobCodeImportPayload } from 'CDK_API';
import { RootState } from 'app/store/rootReducer';
import { getJobCodes as getJobCodesService } from 'app/services/jobCodeService';

// Types

export interface JobCodeState {
  loading: boolean;
  jobCodes: GetJobCodesJobCodePayload[];
  jobCodeImport: GetJobCodesJobCodeImportPayload | null;
  /** The fetch whose result may land. A reset clears it, so a response from before the reset is dropped. */
  requestId: string | null;
}

export const initialState: JobCodeState = {
  loading: false,
  jobCodes: [],
  jobCodeImport: null,
  requestId: null,
};

// Async Thunks

export const fetchJobCodes = createAsyncThunk('jobCode/fetchJobCodes', async (paidolId: string) => {
  const response = await getJobCodesService({
    paidolId,
    nextToken: undefined,
  });

  if (!response?.jobCodeImport) {
    return { jobCodes: [], jobCodeImport: null };
  }

  const activeJobCodes =
    response.jobCodeImport?.jobCodes?.items?.filter(
      (item): item is GetJobCodesJobCodePayload => item?.isActive === true
    ) || [];

  return {
    jobCodes: activeJobCodes,
    jobCodeImport: response.jobCodeImport,
  };
});

// Slice

const jobCodeSlice = createSlice({
  name: 'jobCode',
  initialState,
  reducers: {
    resetJobCodeSlice: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchJobCodes.pending, (state, action) => {
      state.loading = true;
      state.requestId = action.meta.requestId;
    });
    // SEE-1320: only the latest fetch lands. A sign-out resets this slice while a fetch for the
    // previous company may still be in flight; its late result must not fill the next session.
    builder.addCase(fetchJobCodes.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.requestId) return;
      state.loading = false;
      state.jobCodes = action.payload.jobCodes;
      state.jobCodeImport = action.payload.jobCodeImport;
    });
    builder.addCase(fetchJobCodes.rejected, (state, action) => {
      if (action.meta.requestId !== state.requestId) return;
      state.loading = false;
      state.jobCodes = [];
      state.jobCodeImport = null;
    });
  },
});

// Actions & Selectors

export const { resetJobCodeSlice } = jobCodeSlice.actions;

export const selectJobCodeSlice = (state: RootState): JobCodeState => state.jobCode || initialState;

export default jobCodeSlice.reducer;
